import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutGrid,
  ListChecks,
  LifeBuoy,
  Plane,
  FileText,
  Briefcase,
  ShieldCheck,
  Network,
  Cable,
  Activity,
  Users,
  ScrollText,
  LogOut,
  Sparkles,
  Workflow,
  Plug,
  UserPlus,
  Target,
} from "lucide-react";
import Brandmark from "@/components/Brandmark";
import { auth, getToken, setToken, ops } from "@/lib/api";

// Shared user context: pages role-gate money surfaces (P&L, record payment,
// refunds) off the authenticated user AppShell already fetches via /auth/me.
export const UserContext = createContext(null);
export const useUser = () => useContext(UserContext);

const NAV = [
  { to: "/", label: "Work Queue", icon: ListChecks, testid: "nav-queue", badge: true },
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/canvas", label: "Live Canvas", icon: Workflow, testid: "nav-canvas" },
  { to: "/trips", label: "Trip Requests", icon: Plane, testid: "nav-trips" },
  { to: "/quotes", label: "Quotes", icon: FileText, testid: "nav-quotes" },
  { to: "/bookings", label: "Bookings", icon: Briefcase, testid: "nav-bookings" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
  { to: "/ops/connections", label: "Connections", icon: Plug, testid: "nav-connections" },
  { to: "/ops/prospects", label: "Prospects", icon: UserPlus, testid: "nav-prospects" },
  { to: "/ops/strategist", label: "Strategist", icon: Target, testid: "nav-strategist" },
  { to: "/hermes", label: "HERMES Runtime", icon: Network, testid: "nav-hermes" },
  { to: "/adapters", label: "Adapters", icon: Cable, testid: "nav-adapters" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/ops/empty-legs", label: "Empty Legs", icon: Activity, testid: "nav-emptylegs" },
];

function QueueBadge() {
  // Live work-queue count next to the nav item (5-10s ops polling). retry off:
  // a non-money role gets 403 and the badge simply stays hidden.
  const { data } = useQuery({
    queryKey: ["ops-queue-badge"],
    queryFn: ops.queue,
    refetchInterval: 10000,
    retry: false,
  });
  const count = data?.total ?? 0;
  if (!count) return null;
  return (
    <span
      data-testid="queue-badge"
      className="mono shrink-0"
      style={{
        fontSize: 10,
        lineHeight: "16px",
        minWidth: 16,
        padding: "0 4px",
        textAlign: "center",
        background: "rgb(196,52,44)",
        color: "rgb(252,249,243)",
        borderRadius: 999,
      }}
    >
      {count}
    </span>
  );
}

export function AppShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/login", { replace: true });
      return;
    }
    auth.me().then(setUser).catch(() => {
      setToken(null);
      navigate("/login", { replace: true });
    });
     
  }, [location.pathname]);

  const onLogout = () => {
    setToken(null);
    navigate("/login");
  };

  return (
    <UserContext.Provider value={user}>
    <div
      className="noise-overlay flex min-h-screen"
      style={{ background: "rgb(var(--bg))", color: "rgb(var(--fg))" }}
    >
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col"
        style={{
          width: 240,
          borderRight: "1px solid rgb(var(--ln))",
          background: "rgb(var(--surface))",
        }}
      >
        <div className="p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
          <Brandmark />
        </div>

        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 mono transition-colors min-w-0 ${
                    isActive ? "" : "text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))] hover:bg-[rgb(236,229,216)]"
                  }`
                }
                style={({ isActive }) => ({
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  background: isActive
                    ? "linear-gradient(90deg, rgb(201,162,39), rgb(176,110,58))"
                    : undefined,
                  color: isActive ? "rgb(20,17,13)" : undefined,
                  fontWeight: isActive ? 600 : undefined,
                  borderLeft: isActive ? "2px solid rgb(224,196,120)" : "2px solid transparent",
                })}
              >
                <Icon size={14} strokeWidth={1.8} className="flex-shrink-0" />
                <span className="truncate flex-1">{item.label}</span>
                {item.badge && <QueueBadge />}
              </NavLink>
            );
          })}
        </nav>

        <div
          className="p-3"
          style={{ borderTop: "1px solid rgb(var(--ln))" }}
        >
          <a
            href="/widget"
            target="_blank"
            rel="noreferrer"
            data-testid="link-open-widget"
            className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--muted-2))] hover:text-[rgb(var(--fg))] px-2 py-2 min-w-0"
          >
            <Sparkles size={12} className="flex-shrink-0" />
            <span className="truncate">Open Quote Widget</span>
          </a>
          <div className="px-2 py-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div
                className="mono truncate"
                style={{ fontSize: 11, color: "rgb(var(--fg))" }}
                title={user?.name || user?.email || "—"}
              >
                {user?.name || user?.email || "—"}
              </div>
              <div className="pj-label truncate" style={{ fontSize: 9 }} title={user?.role || "—"}>
                {user?.role || "—"}
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              data-testid="btn-logout"
              className="p-1.5 flex-shrink-0 hover:bg-[rgb(236,229,216)] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))]"
              aria-label="Logout"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="pj-fade-in">{children}</div>
      </main>
    </div>
    </UserContext.Provider>
  );
}

export default AppShell;
